// src/services/firebase.js
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import * as topojson from "topojson-client";

// Check if we're in dev mode
const DEV_MODE = import.meta.env.VITE_DEV_MODE === "true";

// ============================================
// FIREBASE CONFIGURATION
// ============================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Only initialize Firebase if not in dev mode
let app, auth, db, storage;

if (!DEV_MODE) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
} else {
  console.log("🔧 DEV MODE: Firebase not initialized, using local data files");
}

export { auth, db, storage };

// ============================================
// DEV MODE MOCK USER
// ============================================

export const mockUser = {
  uid: "dev-admin-user",
  email: "admin@example.com",
  displayName: "Dev Admin",
  role: "admin",
  clientAccess: ["laundry-lifesaver", "demo-client"],
};

// ============================================
// DEV MODE CLIENT CONFIG (v2 Schema)
// ============================================

export const devClientConfig = {
  version: "2.0.0",
  clientSlug: "laundry-lifesaver",

  client: {
    name: "Laundry Lifesaver",
    projectName: "Connecticut Market Analysis",
    contactEmail: "owner@abclaundry.com",
    logoUrl: null,
    primaryColor: "#2c3e50",
    accentColor: "#3498db",
  },

  states: [
    {
      abbreviation: "CT",
      name: "Connecticut",
      fipsCode: "09",
      mapCenter: [41.6032, -73.0877],
      defaultZoom: 9,
      minZoom: 7,
      maxZoom: 18,
    },
  ],

  avatars: [
    {
      id: "self_service",
      name: "Self-Service Laundromat",
      description:
        "Lower-income households, renters, urban areas with foot traffic",
      reports: {
        methodology: {
          title: "Self-Service Methodology",
          fileName: "self_service_methodology.pdf",
          storagePath: "reports/self_service_methodology.pdf",
          icon: "book-open",
        },
      },
    },
    {
      id: "pickup_delivery",
      name: "Pickup & Delivery",
      description: "Affluent families with children, time-poor households",
      reports: {
        methodology: {
          title: "Pickup & Delivery Methodology",
          fileName: "pickup_delivery_methodology.pdf",
          storagePath: "reports/pickup_delivery_methodology.pdf",
          icon: "book-open",
        },
      },
    },
  ],

  maps: [
    {
      id: "ct_zcta_self_service",
      label: "Self-Service Laundromat",
      avatarId: "self_service",
      stateAbbreviation: "CT",
      geographyType: "ZCTA",
      geographyLabel: "ZIP Code",
      geographyLabelPlural: "ZIP Codes",
      data: {
        scoredDataPath: "CT_ZCTA_selfService.json",
        competitorsPath: "competitors/ct_self_service.json",
        opportunitiesPath: "opportunities/ct_opportunities.json",
        topoJsonObjectName: "zcta",
      },
      display: {
        geoIdField: "geoId",
        geoJsonIdField: "GEOID20",
        geoJsonIdPrefix: null,
        labelField: "city",
        secondaryLabelField: "zcta",
        rankField: "rank",
        scoreField: "score",
        tierField: "tier",
        maxScore: 100,
      },
      tierCutoffs: {
        ideal: { min: 85, color: "#4caf50", label: "Ideal" },
        strong: { min: 70, color: "#66bb6a", label: "Strong" },
        highMid: { min: 50, color: "#ffb74d", label: "High mid" },
        lowMid: { min: 30, color: "#ff8a65", label: "Low mid" },
        poor: { min: 0, color: "#f44336", label: "Poor" },
        noData: { min: null, color: "#9e9e9e", label: "No data" },
      },
      popup: {
        titleField: "city",
        subtitleField: "zcta",
        fields: [
          { key: "county", label: "County" },
          { key: "population", label: "Population", format: "number" },
          { key: "households", label: "Households", format: "number" },
        ],
      },
      details: {
        sections: [
          {
            id: "location",
            title: "Location",
            fields: [
              { key: "city", label: "City" },
              { key: "county", label: "County" },
              { key: "state", label: "State" },
            ],
          },
          {
            id: "demographics",
            title: "Demographics",
            fields: [
              { key: "population", label: "Population", format: "number" },
              { key: "households", label: "Households", format: "number" },
            ],
          },
          {
            id: "scoreBreakdown",
            title: "Score Breakdown",
            type: "breakdown",
          },
        ],
      },
      reports: {
        rankedPdf: {
          title: "ZCTA Rankings - Self-Service (PDF)",
          fileName: "CT_ZCTA_self_service_ranked.pdf",
          storagePath: "reports/CT_ZCTA_self_service_ranked.pdf",
          icon: "file-text",
        },
        rankedXlsx: {
          title: "ZCTA Rankings - Self-Service (Excel)",
          fileName: "CT_ZCTA_self_service_ranked.xlsx",
          storagePath: "reports/CT_ZCTA_self_service_ranked.xlsx",
          icon: "table",
        },
        executiveSummary: {
          title: "Executive Summary - ZCTA Self-Service",
          fileName: "executive_summary_CT_ZCTA_self_service.pdf",
          storagePath: "reports/executive_summary_CT_ZCTA_self_service.pdf",
          icon: "file-text",
        },
        competitorsPdf: {
          title: "Competitor List - ZCTA Self-Service (PDF)",
          fileName: "CT_ZCTA_self_service_competitors.pdf",
          storagePath: "reports/CT_ZCTA_self_service_competitors.pdf",
          icon: "file-text",
        },
        competitorsXlsx: {
          title: "Competitor List - ZCTA Self-Service (Excel)",
          fileName: "CT_ZCTA_self_service_competitors.xlsx",
          storagePath: "reports/CT_ZCTA_self_service_competitors.xlsx",
          icon: "table",
        },
      },
      default: true,
    },
    {
      id: "ct_zcta_pickup_delivery",
      label: "Pickup & Delivery",
      avatarId: "pickup_delivery",
      stateAbbreviation: "CT",
      geographyType: "ZCTA",
      geographyLabel: "ZIP Code",
      geographyLabelPlural: "ZIP Codes",
      data: {
        scoredDataPath: "CT_ZCTA_pickupDelivery.json",
        competitorsPath: "competitors/ct_pickup_delivery.json",
        opportunitiesPath: "opportunities/ct_opportunities.json",
        topoJsonObjectName: "zcta",
      },
      display: {
        geoIdField: "geoId",
        geoJsonIdField: "GEOID20",
        geoJsonIdPrefix: null,
        labelField: "city",
        secondaryLabelField: "zcta",
        rankField: "rank",
        scoreField: "score",
        tierField: "tier",
        maxScore: 100,
      },
      tierCutoffs: {
        ideal: { min: 85, color: "#4caf50", label: "Ideal" },
        strong: { min: 70, color: "#66bb6a", label: "Strong" },
        highMid: { min: 50, color: "#ffb74d", label: "High mid" },
        lowMid: { min: 30, color: "#ff8a65", label: "Low mid" },
        poor: { min: 0, color: "#f44336", label: "Poor" },
        noData: { min: null, color: "#9e9e9e", label: "No data" },
      },
      popup: {
        titleField: "city",
        subtitleField: "zcta",
        fields: [
          { key: "county", label: "County" },
          { key: "population", label: "Population", format: "number" },
          { key: "households", label: "Households", format: "number" },
        ],
      },
      details: {
        sections: [
          {
            id: "location",
            title: "Location",
            fields: [
              { key: "city", label: "City" },
              { key: "county", label: "County" },
              { key: "state", label: "State" },
            ],
          },
          {
            id: "demographics",
            title: "Demographics",
            fields: [
              { key: "population", label: "Population", format: "number" },
              { key: "households", label: "Households", format: "number" },
            ],
          },
          {
            id: "scoreBreakdown",
            title: "Score Breakdown",
            type: "breakdown",
          },
        ],
      },
      reports: {
        rankedPdf: {
          title: "ZCTA Rankings - Pickup & Delivery (PDF)",
          fileName: "CT_ZCTA_pickup_Delivery_ranked.pdf",
          storagePath: "reports/CT_ZCTA_pickup_Delivery_ranked.pdf",
          icon: "file-text",
        },
        rankedXlsx: {
          title: "ZCTA Rankings - Pickup & Delivery (Excel)",
          fileName: "CT_ZCTA_pickup_Delivery_ranked.xlsx",
          storagePath: "reports/CT_ZCTA_pickup_Delivery_ranked.xlsx",
          icon: "table",
        },
        executiveSummary: {
          title: "Executive Summary - ZCTA Pickup & Delivery",
          fileName: "executive_summary_CT_ZCTA_pickup_Delivery.pdf",
          storagePath: "reports/executive_summary_CT_ZCTA_pickup_Delivery.pdf",
          icon: "file-text",
        },
        competitorsPdf: {
          title: "Competitor List - ZCTA Pickup & Delivery(PDF)",
          fileName: "CT_ZCTA_pickup_delivery_competitors.pdf",
          storagePath: "reports/CT_ZCTA_pickup_delivery_competitors.pdf",
          icon: "file-text",
        },
        competitorsXlsx: {
          title: "Competitor List - ZCTA Pickup & Delivery(Excel)",
          fileName: "CT_ZCTA_pickup_delivery_competitors.xlsx",
          storagePath: "reports/CT_ZCTA_pickup_delivery_competitors.xlsx",
          icon: "table",
        },
      },
      default: false,
    },
    {
      id: "ct_bg_self_service",
      label: "Self-Service Laundromat",
      avatarId: "self_service",
      stateAbbreviation: "CT",
      geographyType: "BG",
      geographyLabel: "Block Group",
      geographyLabelPlural: "Block Groups",
      data: {
        scoredDataPath: "CT_BG_selfService.json",
        competitorsPath: "competitors/ct_self_service.json",
        opportunitiesPath: "opportunities/ct_opportunities.json",
        topoJsonObjectName: "zcta",
      },
      display: {
        geoIdField: "geoId",
        geoJsonIdField: "GEOIDFQ",
        geoJsonIdPrefix: "1500000US",
        labelField: "city",
        secondaryLabelField: "geoId",
        rankField: "rank",
        scoreField: "score",
        tierField: "tier",
        maxScore: 100,
      },
      tierCutoffs: {
        ideal: { min: 85, color: "#4caf50", label: "Ideal" },
        strong: { min: 70, color: "#66bb6a", label: "Strong" },
        highMid: { min: 50, color: "#ffb74d", label: "High mid" },
        lowMid: { min: 30, color: "#ff8a65", label: "Low mid" },
        poor: { min: 0, color: "#f44336", label: "Poor" },
        noData: { min: null, color: "#9e9e9e", label: "No data" },
      },
      popup: {
        titleField: "city",
        subtitleField: "geoId",
        fields: [
          { key: "county", label: "County" },
          { key: "population", label: "Population", format: "number" },
          { key: "households", label: "Households", format: "number" },
        ],
      },
      details: {
        sections: [
          {
            id: "location",
            title: "Location",
            fields: [
              { key: "city", label: "City" },
              { key: "county", label: "County" },
              { key: "state", label: "State" },
            ],
          },
          {
            id: "demographics",
            title: "Demographics",
            fields: [
              { key: "population", label: "Population", format: "number" },
              { key: "households", label: "Households", format: "number" },
            ],
          },
          { id: "scoreBreakdown", title: "Score Breakdown", type: "breakdown" },
        ],
      },
      reports: {
        rankedPdf: {
          title: "Block Group Rankings - Self-Service (PDF)",
          fileName: "CT_BG_self_service_ranked.pdf",
          storagePath: "reports/CT_BG_self_service_ranked.pdf",
          icon: "file-text",
        },
        rankedXlsx: {
          title: "Block Group Rankings - Self-Service (Excel)",
          fileName: "CT_BG_self_service_ranked.xlsx",
          storagePath: "reports/CT_BG_self_service_ranked.xlsx",
          icon: "table",
        },
        executiveSummary: {
          title: "Executive Summary - BG Self-Service",
          fileName: "executive_summary_CT_BG_self_service.pdf",
          storagePath: "reports/executive_summary_CT_BG_self_service.pdf",
          icon: "file-text",
        },
        competitorsPdf: {
          title: "Competitor List - BG Self-Service (PDF)",
          fileName: "CT_BG_self_service_competitors.pdf",
          storagePath: "reports/CT_BG_self_service_competitors.pdf",
          icon: "file-text",
        },
        competitorsXlsx: {
          title: "Competitor List - BG Self-Service (Excel)",
          fileName: "CT_BG_self_service_competitors.xlsx",
          storagePath: "reports/CT_BG_self_service_competitors.xlsx",
          icon: "table",
        },
      },
      default: false,
    },
    {
      id: "ct_bg_pickup_delivery",
      label: "Pickup & Delivery",
      avatarId: "pickup_delivery",
      stateAbbreviation: "CT",
      geographyType: "BG",
      geographyLabel: "Block Group",
      geographyLabelPlural: "Block Groups",
      data: {
        scoredDataPath: "CT_BG_pickupDelivery.json",
        competitorsPath: "competitors/ct_pickup_delivery.json",
        opportunitiesPath: "opportunities/ct_opportunities.json",
        topoJsonObjectName: "zcta",
      },
      display: {
        geoIdField: "geoId",
        geoJsonIdField: "GEOIDFQ",
        geoJsonIdPrefix: "1500000US",
        labelField: "city",
        secondaryLabelField: "geoId",
        rankField: "rank",
        scoreField: "score",
        tierField: "tier",
        maxScore: 100,
      },
      tierCutoffs: {
        ideal: { min: 85, color: "#4caf50", label: "Ideal" },
        strong: { min: 70, color: "#66bb6a", label: "Strong" },
        highMid: { min: 50, color: "#ffb74d", label: "High mid" },
        lowMid: { min: 30, color: "#ff8a65", label: "Low mid" },
        poor: { min: 0, color: "#f44336", label: "Poor" },
        noData: { min: null, color: "#9e9e9e", label: "No data" },
      },
      popup: {
        titleField: "city",
        subtitleField: "geoId",
        fields: [
          { key: "county", label: "County" },
          { key: "population", label: "Population", format: "number" },
          { key: "households", label: "Households", format: "number" },
        ],
      },
      details: {
        sections: [
          {
            id: "location",
            title: "Location",
            fields: [
              { key: "city", label: "City" },
              { key: "county", label: "County" },
              { key: "state", label: "State" },
            ],
          },
          {
            id: "demographics",
            title: "Demographics",
            fields: [
              { key: "population", label: "Population", format: "number" },
              { key: "households", label: "Households", format: "number" },
            ],
          },
          { id: "scoreBreakdown", title: "Score Breakdown", type: "breakdown" },
        ],
      },
      reports: {
        rankedPdf: {
          title: "Block Group Rankings - Pickup & Delivery (PDF)",
          fileName: "CT_BG_pickup_Delivery_ranked.pdf",
          storagePath: "reports/CT_BG_pickup_Delivery_ranked.pdf",
          icon: "file-text",
        },
        rankedXlsx: {
          title: "Block Group Rankings - Pickup & Delivery (Excel)",
          fileName: "CT_BG_pickup_Delivery_ranked.xlsx",
          storagePath: "reports/CT_BG_pickup_Delivery_ranked.xlsx",
          icon: "table",
        },
        executiveSummary: {
          title: "Executive Summary - BG Pickup & Delivery",
          fileName: "executive_summary_CT_BG_pickup_Delivery.pdf",
          storagePath: "reports/executive_summary_CT_BG_pickup_Delivery.pdf",
          icon: "file-text",
        },
        competitorsPdf: {
          title: "Competitor List - BG Pickup & Delivery(PDF)",
          fileName: "CT_BG_pickup_delivery_competitors.pdf",
          storagePath: "reports/CT_BG_pickup_delivery_competitors.pdf",
          icon: "file-text",
        },
        competitorsXlsx: {
          title: "Competitor List - BG Pickup & Delivery(Excel)",
          fileName: "CT_BG_pickup_delivery_competitors.xlsx",
          storagePath: "reports/CT_BG_pickup_delivery_competitors.xlsx",
          icon: "table",
        },
      },
      default: false,
    },
  ],

  globalReports: [
    {
      id: "faq",
      title: "Frequently Asked Questions",
      fileName: "faq.md",
      storagePath: "reports/faq.md",
      type: "markdown",
      icon: "help-circle",
    },
  ],

  features: {
    competitors: { enabled: true, label: "Competitors" },
    opportunities: { enabled: true, label: "Opportunities" },
    proxyBusinesses: { enabled: false },
    search: {
      enabled: true,
      searchableFields: ["city", "zcta", "geoId", "county"],
    },
    download: { enabled: true, formats: ["xlsx", "csv"] },
    reports: { enabled: true },
  },
};

// ============================================
// AUTHENTICATION FUNCTIONS
// ============================================

/**
 * Sign in with email and password
 */
export const signIn = async (email, password) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Skipping Firebase auth");
    return mockUser;
  }
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password,
  );
  return userCredential.user;
};

/**
 * Sign out
 */
export const signOut = async () => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock sign out");
    return;
  }
  await firebaseSignOut(auth);
};

/**
 * Send password reset email
 */
export const resetPassword = async (email) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock password reset for", email);
    return;
  }
  await sendPasswordResetEmail(auth, email);
};

/**
 * Observe authentication state changes
 */
export const observeAuthState = (callback) => {
  if (DEV_MODE) {
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};

// ============================================
// USER FUNCTIONS
// ============================================

/**
 * Get user profile from Firestore
 */
export const getUserProfile = async (uid) => {
  if (DEV_MODE) {
    return mockUser;
  }

  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting user profile:", error);
    throw error;
  }
};

/**
 * Update user's last login timestamp
 */
export const updateLastLogin = async (uid) => {
  if (DEV_MODE) return;

  try {
    await updateDoc(doc(db, "users", uid), {
      lastLogin: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error updating last login:", error);
  }
};

// ============================================
// CLIENT CONFIGURATION FUNCTIONS
// ============================================

/**
 * Load client configuration from Firestore (or dev config)
 */
export const loadClientConfig = async (clientSlug) => {
  if (DEV_MODE) {
    console.log(`🔧 DEV MODE: Loading dev config for "${clientSlug}"`);
    await new Promise((resolve) => setTimeout(resolve, 200));
    return { ...devClientConfig, clientSlug };
  }

  try {
    const clientDoc = await getDoc(doc(db, "clients", clientSlug));
    if (!clientDoc.exists()) {
      return null;
    }

    const data = clientDoc.data();
    if (data.status !== "active") {
      throw new Error("This client account is inactive.");
    }

    return data.config;
  } catch (error) {
    console.error("Error loading client config:", error);
    throw error;
  }
};

// ============================================
// DATA LOADING FUNCTIONS
// ============================================

const dataCache = new Map();

/**
 * Convert TopoJSON to GeoJSON
 * @param {Object} topoData - TopoJSON data
 * @param {string} objectName - Name of the object in TopoJSON (e.g., "zcta")
 * @returns {Object} GeoJSON FeatureCollection
 */
const topoToGeo = (topoData, objectName = "zcta") => {
  if (!topoData || !topoData.objects) {
    throw new Error("Invalid TopoJSON data");
  }

  // Find the object - try exact match first, then case-insensitive
  let obj = topoData.objects[objectName];
  if (!obj) {
    const key = Object.keys(topoData.objects).find(
      (k) => k.toLowerCase() === objectName.toLowerCase(),
    );
    if (key) {
      obj = topoData.objects[key];
    }
  }

  if (!obj) {
    // Use first available object
    const keys = Object.keys(topoData.objects);
    if (keys.length > 0) {
      console.warn(
        `TopoJSON object "${objectName}" not found, using "${keys[0]}"`,
      );
      obj = topoData.objects[keys[0]];
    } else {
      throw new Error(`No objects found in TopoJSON`);
    }
  }

  return topojson.feature(topoData, obj);
};

/**
 * Load TopoJSON/GeoJSON from Firebase Storage or local dev files
 * @param {string} path - Storage path
 * @param {string} objectName - TopoJSON object name (for conversion)
 */
export const loadGeoJSON = async (path, objectName = "zcta") => {
  const cacheKey = `geojson_${path}`;

  if (dataCache.has(cacheKey)) {
    console.log(`📦 Loading GeoJSON from cache: ${path}`);
    return dataCache.get(cacheKey);
  }

  if (DEV_MODE) {
    console.log(`🔧 DEV MODE: Loading GeoJSON from /data/...`);

    // Extract state and geoType from path
    // Expected path format: "shared/geojson/ZCTA/CT.json" or similar
    const pathParts = path.split("/");
    const geoType = pathParts[pathParts.length - 2] || "ZCTA";
    const fileName = pathParts[pathParts.length - 1] || "CT.json";

    const localPath = `/data/geojson/${geoType}/${fileName}`;
    console.log(`🔧 Fetching: ${localPath}`);

    try {
      const response = await fetch(localPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${localPath}: ${response.status}`);
      }

      const data = await response.json();

      // Check if it's TopoJSON (has "type": "Topology") and convert
      let geoJSON;
      if (data.type === "Topology") {
        console.log(
          `🔧 Converting TopoJSON to GeoJSON (object: ${objectName})`,
        );
        geoJSON = topoToGeo(data, objectName);
      } else {
        geoJSON = data;
      }

      dataCache.set(cacheKey, geoJSON);
      console.log(`✅ Loaded ${geoJSON.features?.length || 0} features`);
      return geoJSON;
    } catch (error) {
      console.error("Error loading GeoJSON:", error);
      throw error;
    }
  }

  // Production: load from Firebase Storage
  try {
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch GeoJSON: ${response.status}`);
    }

    const data = await response.json();

    // Convert if TopoJSON
    let geoJSON;
    if (data.type === "Topology") {
      geoJSON = topoToGeo(data, objectName);
    } else {
      geoJSON = data;
    }

    dataCache.set(cacheKey, geoJSON);
    return geoJSON;
  } catch (error) {
    console.error("Error loading GeoJSON from Firebase:", error);
    throw error;
  }
};

/**
 * Load scored data from Firebase Storage or local dev files
 * @param {string} path - Storage path
 */
export const loadScoredData = async (path) => {
  const cacheKey = `scored_${path}`;

  if (dataCache.has(cacheKey)) {
    console.log(`📦 Loading scored data from cache: ${path}`);
    return dataCache.get(cacheKey);
  }

  if (DEV_MODE) {
    console.log(`🔧 DEV MODE: Loading scored data...`);

    // Extract filename from path
    // Expected path format: "clients/laundry-lifesaver/scored/CT_ZCTA_selfService.json"
    const fileName = path.split("/").pop();
    const localPath = `/data/scored/${fileName}`;
    console.log(`🔧 Fetching: ${localPath}`);

    try {
      const response = await fetch(localPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${localPath}: ${response.status}`);
      }

      const data = await response.json();
      dataCache.set(cacheKey, data);
      console.log(`✅ Loaded ${data.geographies?.length || 0} geographies`);
      return data;
    } catch (error) {
      console.error("Error loading scored data:", error);
      throw error;
    }
  }

  // Production: load from Firebase Storage
  try {
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch scored data: ${response.status}`);
    }

    const data = await response.json();
    dataCache.set(cacheKey, data);
    return data;
  } catch (error) {
    console.error("Error loading scored data from Firebase:", error);
    throw error;
  }
};

/**
 * Load competitor data from Firebase Storage or local dev files.
 * Follows the same pattern as loadScoredData().
 *
 * @param {string} path - Full Storage path (e.g., "clients/laundry-lifesaver/competitors/ct_self_service.json")
 * @returns {Promise<Object>} Parsed competitor JSON (with metadata and competitors array)
 */
export const loadCompetitorData = async (path) => {
  const cacheKey = `competitors_${path}`;

  if (dataCache.has(cacheKey)) {
    console.log(`📦 Loading competitor data from cache: ${path}`);
    return dataCache.get(cacheKey);
  }

  if (DEV_MODE) {
    console.log(`🔧 DEV MODE: Loading competitor data...`);

    // Extract filename from path
    // Expected path format: "clients/laundry-lifesaver/competitors/ct_self_service.json"
    const fileName = path.split("/").pop();
    const localPath = `/data/competitors/${fileName}`;
    console.log(`🔧 Fetching: ${localPath}`);

    try {
      const response = await fetch(localPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${localPath}: ${response.status}`);
      }

      const data = await response.json();
      dataCache.set(cacheKey, data);
      console.log(`✅ Loaded ${data.competitors?.length || 0} competitors`);
      return data;
    } catch (error) {
      console.error("Error loading competitor data:", error);
      throw error;
    }
  }

  // Production: load from Firebase Storage
  try {
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch competitor data: ${response.status}`);
    }

    const data = await response.json();
    dataCache.set(cacheKey, data);
    return data;
  } catch (error) {
    console.error("Error loading competitor data from Firebase:", error);
    throw error;
  }
};

/**
 * Load opportunity data from Firebase Storage or local dev files.
 * Follows the same pattern as loadCompetitorData().
 *
 * @param {string} path - Full Storage path (e.g., "clients/laundry-lifesaver/opportunities/ct_opportunities.json")
 * @returns {Promise<Object>} Parsed opportunity JSON (with metadata and opportunities array)
 */
export const loadOpportunityData = async (path) => {
  const cacheKey = `opportunities_${path}`;

  if (dataCache.has(cacheKey)) {
    console.log(`📦 Loading opportunity data from cache: ${path}`);
    return dataCache.get(cacheKey);
  }

  if (DEV_MODE) {
    console.log(`🔧 DEV MODE: Loading opportunity data...`);

    // Extract filename from path
    // Expected path format: "clients/laundry-lifesaver/opportunities/ct_opportunities.json"
    const fileName = path.split("/").pop();
    const localPath = `/data/opportunities/${fileName}`;
    console.log(`🔧 Fetching: ${localPath}`);

    try {
      const response = await fetch(localPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${localPath}: ${response.status}`);
      }

      const data = await response.json();
      dataCache.set(cacheKey, data);
      console.log(`✅ Loaded ${data.opportunities?.length || 0} opportunities`);
      return data;
    } catch (error) {
      console.error("Error loading opportunity data:", error);
      throw error;
    }
  }

  // Production: load from Firebase Storage
  try {
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch opportunity data: ${response.status}`);
    }

    const data = await response.json();
    dataCache.set(cacheKey, data);
    return data;
  } catch (error) {
    console.error("Error loading opportunity data from Firebase:", error);
    throw error;
  }
};

/**
 * Get a download URL for a report file in Firebase Storage.
 * storagePath is relative to /clients/{clientSlug}/ per v2 convention.
 *
 * @param {string} clientSlug - The client's URL slug
 * @param {string} storagePath - Path relative to the client's storage dir (e.g. "reports/faq.md")
 * @returns {Promise<string>} Download URL
 */
export const getReportDownloadUrl = async (clientSlug, storagePath) => {
  if (DEV_MODE) {
    console.log(`🔧 DEV MODE: Mock report URL for ${storagePath}`);
    // Map storagePath to local dev file paths
    // storagePath looks like "reports/CT_ZCTA_self_service_ranked.pdf"
    const fileName = storagePath.split("/").pop();
    const ext = fileName.split(".").pop().toLowerCase();

    // Try to match to local /reports/ folder structure
    if (ext === "md") return `/reports/faq/${fileName}`;
    if (ext === "xlsx") return `/reports/excel_ranked/${fileName}`;
    if (fileName.startsWith("executive_summary"))
      return `/reports/executive_summaries/${fileName}`;
    if (fileName.includes("methodology"))
      return `/reports/methodology/${fileName}`;
    if (ext === "pdf") return `/reports/pdf_ranked/${fileName}`;

    return `#`;
  }

  const fullPath = `clients/${clientSlug}/${storagePath}`;
  const storageRef = ref(storage, fullPath);
  return getDownloadURL(storageRef);
};

/**
 * Load raw markdown content from Firebase Storage (or local dev files).
 * Used by the FAQ page to fetch and render markdown as HTML.
 *
 * @param {string} clientSlug - The client's URL slug
 * @param {string} storagePath - Path relative to the client's storage dir (e.g. "reports/faq.md")
 * @returns {Promise<string>} Raw markdown text
 */
export const loadMarkdownContent = async (clientSlug, storagePath) => {
  if (DEV_MODE) {
    const fileName = storagePath.split("/").pop();
    const localPath = `/reports/faq/${fileName}`;
    console.log(`🔧 DEV MODE: Loading markdown from ${localPath}`);

    const response = await fetch(localPath);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${localPath}: ${response.status}`);
    }
    return response.text();
  }

  const fullPath = `clients/${clientSlug}/${storagePath}`;
  const storageRef = ref(storage, fullPath);
  const url = await getDownloadURL(storageRef);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch markdown: ${response.status}`);
  }

  return response.text();
};

// ============================================
// CACHE MANAGEMENT
// ============================================

/**
 * Clear all cached data
 */
export const clearCache = () => {
  dataCache.clear();
  console.log("🧹 Data cache cleared");
};

/**
 * Clear cache for a specific path
 */
export const clearCacheForPath = (path) => {
  const keys = Array.from(dataCache.keys()).filter((k) => k.includes(path));
  keys.forEach((k) => dataCache.delete(k));
};

export default app;
